import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import type { WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const TYPES_QUERY_KEY = ["contacts", "types"];

/**
 * Turn one `WriteRejection` into a sentence someone can act on.
 *
 * `WriteResult` exists specifically so a caller cannot forget a rejection
 * kind — a switch with no `default` means adding a sixth kind to the union
 * in `@estelle/domain` fails this file at compile time instead of silently
 * falling through to a generic "something went wrong."
 */
function describeRejection(rejection: WriteRejection): string {
  switch (rejection.kind) {
    case "blocked":
    case "channel_rejected":
      return rejection.message;
    case "conflict":
      return `${rejection.message} (it changed at ${rejection.currentUpdatedAt})`;
    case "forbidden":
      return `${rejection.message} (requires ${rejection.permission})`;
    case "invalid":
      return rejection.fields
        ? `${rejection.message} ${Object.entries(rejection.fields)
            .map(([field, problem]) => `${field}: ${problem}`)
            .join("; ")}`
        : rejection.message;
  }
}

/**
 * Contact type management (FR-CON-4).
 *
 * Vendor, Customer and Supplier ship with the system and cannot be deleted —
 * the delete button is disabled for them so the common case never reaches
 * the adapter, but the rejection path below still renders if it ever does,
 * because a disabled button is a courtesy, not the enforcement. The adapter
 * is what actually refuses (`blocked`), same as every other write in this
 * application.
 */
export function ContactTypes() {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const typesQuery = useQuery({
    queryKey: TYPES_QUERY_KEY,
    queryFn: () => data.contacts.listTypes(),
  });

  const [newName, setNewName] = useState("");
  const [createRejection, setCreateRejection] = useState<WriteRejection | null>(
    null,
  );

  const createMutation = useMutation({
    mutationFn: (name: string) => data.contacts.createType(name),
    onSuccess: (result) => {
      if (result.ok) {
        setNewName("");
        setCreateRejection(null);
        void queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      } else {
        setCreateRejection(result.rejection);
      }
    },
  });

  const [renaming, setRenaming] = useState<{
    id: string;
    draft: string;
  } | null>(null);
  const [renameRejection, setRenameRejection] = useState<WriteRejection | null>(
    null,
  );

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      data.contacts.renameType(id, name),
    onSuccess: (result) => {
      if (result.ok) {
        setRenaming(null);
        setRenameRejection(null);
        void queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      } else {
        setRenameRejection(result.rejection);
      }
    },
  });

  const [deleteRejections, setDeleteRejections] = useState<
    Record<string, WriteRejection>
  >({});

  const deleteMutation = useMutation({
    mutationFn: (id: string) => data.contacts.deleteType(id),
    onSuccess: (result, id) => {
      if (result.ok) {
        setDeleteRejections((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([existingId]) => existingId !== id),
          ),
        );
        void queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      } else {
        setDeleteRejections((prev) => ({ ...prev, [id]: result.rejection }));
      }
    },
  });

  if (typesQuery.isPending) return <TableSkeleton rows={5} />;

  if (typesQuery.isError) {
    return (
      <LoadFailed
        message={typesQuery.error.message}
        onRetry={() => {
          void typesQuery.refetch();
        }}
      />
    );
  }

  return (
    <div className="max-w-xl space-y-4">
      <form
        className="flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = newName.trim();
          if (trimmed === "") return;
          createMutation.mutate(trimmed);
        }}
      >
        <div className="flex-1 space-y-1">
          <Label htmlFor="new-contact-type">New type</Label>
          <Input
            id="new-contact-type"
            value={newName}
            onChange={(event) => {
              setNewName(event.target.value);
            }}
            placeholder="e.g. Photographer"
          />
        </div>
        <Button
          type="submit"
          disabled={newName.trim() === "" || createMutation.isPending}
        >
          <Plus />
          Add type
        </Button>
      </form>
      {createRejection ? (
        <p className="text-destructive text-sm">
          {describeRejection(createRejection)}
        </p>
      ) : null}

      <ul className="divide-border divide-y rounded-md border">
        {typesQuery.data.map((type) => {
          const isRenaming = renaming?.id === type.id;
          const deleteRejection = deleteRejections[type.id];
          const isDeleting =
            deleteMutation.isPending && deleteMutation.variables === type.id;

          return (
            <li key={type.id} className="flex flex-col gap-1 px-3 py-2">
              <div className="flex items-center gap-2">
                {isRenaming ? (
                  <>
                    <Input
                      autoFocus
                      className="h-8"
                      value={renaming.draft}
                      onChange={(event) => {
                        setRenaming({ id: type.id, draft: event.target.value });
                      }}
                    />
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      disabled={renaming.draft.trim() === ""}
                      onClick={() => {
                        renameMutation.mutate({
                          id: type.id,
                          name: renaming.draft.trim(),
                        });
                      }}
                    >
                      <Check />
                    </Button>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => {
                        setRenaming(null);
                        setRenameRejection(null);
                      }}
                    >
                      <X />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm">{type.name}</span>
                    {type.isSystem ? (
                      <Badge variant="secondary">System</Badge>
                    ) : null}
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => {
                        setRenaming({ id: type.id, draft: type.name });
                        setRenameRejection(null);
                      }}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      disabled={type.isSystem || isDeleting}
                      onClick={() => {
                        deleteMutation.mutate(type.id);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </>
                )}
              </div>

              {isRenaming && renameRejection ? (
                <p className="text-destructive text-xs">
                  {describeRejection(renameRejection)}
                </p>
              ) : null}
              {deleteRejection ? (
                <p className="text-destructive text-xs">
                  {describeRejection(deleteRejection)}
                </p>
              ) : null}
              {type.isSystem ? (
                <p className="text-muted-foreground text-xs">
                  Ships with the system — cannot be deleted.
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
